import type { ChatParticipantId, Conversation, Message } from '@getaccept/dsr-shared';
import { apolloClient } from '@getaccept/signing-site/src/apollo';
import { ConversationQueries } from './graphql/conversation.queries';
import { ConversationMutations } from './graphql/conversation.mutations';

export class ChatService {
  static async loadConversationList(): Promise<Conversation[]> {
    const { data } = await apolloClient.query({
      query: ConversationQueries.loadList,
      fetchPolicy: 'network-only',
    });
    return data?.conversations || [];
  }

  static async loadConversationDetails(conversationId: string): Promise<Conversation> {
    const {
      data: { conversation },
    } = await apolloClient.query({
      query: ConversationQueries.loadDetailed,
      variables: { conversationId },
      fetchPolicy: 'network-only',
    });
    return conversation;
  }

  static async addMessage(
    conversationId: string,
    text: string,
    repliedTo?: string
  ): Promise<Message> {
    const {
      data: { addMessage },
      errors,
    } = await apolloClient.mutate({
      mutation: ConversationMutations.addMessage,
      variables: { payload: { conversationId, text, repliedTo } },
    });
    if (errors?.length) {
      throw errors[0];
    }
    return addMessage;
  }

  static async setMessageReadBy(conversationId: string, messageId: string): Promise<void> {
    await apolloClient.mutate({
      mutation: ConversationMutations.setMessageReadBy,
      variables: { payload: { conversationId, messageId } },
    });
  }

  static async deleteMessage(conversationId: string, messageId: string): Promise<boolean> {
    const { data, errors } = await apolloClient.mutate({
      mutation: ConversationMutations.deleteMessage,
      variables: { payload: { conversationId, messageId } },
    });
    return data?.deleteMessage && !errors?.length;
  }

  static async setTypingStatus(conversationId: string, isTyping: boolean): Promise<void> {
    await apolloClient.mutate({
      mutation: ConversationMutations.typingStatus,
      variables: { conversationId, isTyping },
    });
  }

  static async createConversation(participants: ChatParticipantId[]): Promise<Conversation> {
    const {
      data: { createConversation },
    } = await apolloClient.mutate({
      mutation: ConversationMutations.createConversation,
      variables: { payload: { participants } },
    });
    return createConversation;
  }
}
